import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Input from '@mui/material/Input';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import { RiEyeFill, RiEyeOffFill } from 'react-icons/ri';
import FormHelperText from '@mui/material/FormHelperText';
import { MdDeleteForever } from 'react-icons/md';

import { getUsers, updateUser, deleteUser } from '../../api/admin';
import { useNotification } from '../../context/notification';
import convertTinyInt from '../../utils';

function Users() {
  const [formData, setFormData] = useState([]);
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();

  const handleOnChange = (key, value, userID) => {
    const updatedState = formData.map((user) => {
      if (user.userID === userID) {
        if (key !== 'password') return { ...user, [key]: value, asChanged: true };
        const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;
        if (strongRegex.test(value)) return { ...user, [key]: value, error: false, asChanged: true };
        return { ...user, [key]: value, error: true, asChanged: true };
      }
      return user;
    });
    setFormData(updatedState);
  };

  const { isLoading: usersIsLoading } = useQuery('users', getUsers, {
    onSuccess: (data) => {
      setFormData(
        data.map((user) => ({ ...user, isEditing: false, password: '', showPassword: false, error: false, asChanged: false })),
      );
    },
  });

  const { mutate: update, isLoading: updateIsLoading } = useMutation((data) => updateUser(data), {
    onSuccess: (data) => {
      addNotification({ message: data.message });
    },
    onError: (err) => {
      addNotification({ message: `Une erreur est survenue: ${err.message}`, severity: 'error' });
    },
  });

  const { mutate: mutateDelete, isLoading: deleteIsLoading } = useMutation((data) => deleteUser(data), {
    onSuccess: (data) => {
      addNotification({ message: data.message });
      queryClient.invalidateQueries('users');
    },
    onError: (err) => {
      addNotification({ message: `Une erreur est survenue: ${err.message}`, severity: 'error' });
    },
  });

  const rights = [
    { label: 'Admin', value: 'admin' },
    { label: 'Promo - VOD', value: 'promo' },
    { label: 'EVENT', value: 'event' },
    { label: 'Thème', value: 'theme' },
    { label: 'Français', value: 'fr' },
    { label: 'Anglais', value: 'en' },
    { label: 'Arabe', value: 'ar' },
    { label: 'Espagnol', value: 'es' },
  ];

  return (
    <section className="flex items-center justify-center flex-wrap md:space-x-6 md:space-y-4 overflow-auto authForm">
      {usersIsLoading ? (
        <CircularProgress />
      ) : (
        formData.map((user) => (
          <div key={`user_${user.userID}`} className="bg-black p-8 space-y-4 rounded" style={{ maxWidth: '343px' }}>
            <span>{user.username}</span>
            <FormControl fullWidth variant="standard" error={user.error}>
              <InputLabel>Mot de passe</InputLabel>
              <Input
                autoComplete="off"
                id="standard-adornment-password"
                type={user.showPassword ? 'text' : 'password'}
                onChange={(e) => handleOnChange('password', e.target.value, user.userID)}
                size="small"
                value={user.password}
                endAdornment={
                  <button
                    type="button"
                    className="focus"
                    aria-label="toggle password visibility"
                    onClick={() => handleOnChange('showPassword', !user.showPassword, user.userID)}
                  >
                    {user.showPassword ? <RiEyeOffFill color="white" /> : <RiEyeFill color="white" />}
                  </button>
                }
              />
              {user.error && (
                <FormHelperText>
                  Le mot de passe doit contenir: minuscule, majuscule, chiffre, caractère spécial, 8 caractères minimums.
                </FormHelperText>
              )}
            </FormControl>

            <div className="flex flex-col userRights">
              {rights.map((lang) => (
                <FormControlLabel
                  key={`${user.username} ${lang.value}`}
                  control={
                    <Checkbox
                      checked={convertTinyInt(user[lang.value])}
                      size="small"
                      onChange={(e) => handleOnChange(lang.value, e.target.checked, user.userID)}
                      sx={{ padding: '0 0.5rem' }}
                    />
                  }
                  label={lang.label}
                />
              ))}
            </div>
            <div className="space-x-2 mt-8 flex items-center">
              <button
                type="button"
                disabled={!user.asChanged}
                onClick={() => {
                  if (user.error) {
                    return addNotification({
                      message:
                        'Le mot de passe doit contenir: minuscule, majuscule, chiffre, caractère spécial, 8 caractères minimums.',
                      severity: 'error',
                    });
                  }
                  update(user);
                }}
                className="px-4 py-2 rounded bg-zinc-900 hover:bg-blue-500 duration-500 disabled:opacity-50 disabled:hover:bg-zinc-900"
              >
                {updateIsLoading ? <CircularProgress size={13} /> : 'Valider'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (user.error) {
                    return addNotification({
                      message:
                        'Le mot de passe doit contenir: minuscule, majuscule, chiffre, caractère spécial, 8 caractères minimums.',
                      severity: 'error',
                    });
                  }
                  mutateDelete({ username: user.username, id: user.userID });
                }}
                className="px-2 py-2 rounded bg-red-800 hover:bg-red-500 duration-500 disabled:opacity-50 disabled:hover:bg-red-900"
              >
                {deleteIsLoading ? <CircularProgress size={13} /> : <MdDeleteForever color="white" />}
              </button>
            </div>
          </div>
        ))
      )}
    </section>
  );
}

Users.propTypes = {};

export default Users;
